import type { DateRange } from "@/types/api";
import { useMemo } from "react";

/**
 * A STABLE date range for a rolling window of N days.
 *
 * Computing `new Date(Date.now() - days * 86_400_000)` inline looks harmless
 * and is not: it runs on every render and differs by milliseconds each time,
 * so it becomes a new React Query key, which refetches, which re-renders,
 * which makes another key. The screen spams the API and never leaves its
 * loading state.
 *
 * Two things prevent that here. The value is memoized on `days`, and it is
 * rounded to the start of the day — so it is identical across every render
 * and only moves when the user picks a different window (or the date rolls
 * over), which is the only time a refetch is actually wanted.
 */
export function useDateRange(days: number): DateRange {
	return useMemo(() => {
		const d = new Date();
		d.setHours(0, 0, 0, 0);
		// Inclusive: "7 days" means today plus the six before it.
		d.setDate(d.getDate() - (days - 1));
		return { from: d.toISOString() };
	}, [days]);
}
